import { ApiError } from './client';
import type { BrowserUploadSource, UploadState } from './fileBrowserUpload';
import { AdaptiveUploadConcurrency, UploadSlots } from './uploadConcurrency';

export const PARALLEL_PROTOCOL = 'parallel-ranges-v1';
export const ADAPTIVE_PROTOCOL = 'parallel-ranges-v2';
export interface UploadLane { start: number; end: number; received: number }
const MAX_CHUNK = 4 * 1024 * 1024;

// Shared by every file in this page, including callers outside TransferTray.
const pageSlots = new UploadSlots(16);

interface Options {
  connectionLimit?: () => number;
  file: BrowserUploadSource;
  initial: UploadState;
  read: (start: number, end: number, signal: AbortSignal) => Promise<Blob>;
  send: (lane: number, offset: number, chunk: File, sha256: string, signal: AbortSignal) => Promise<UploadState>;
  onProgress?: (received: number) => void;
  onStatus?: (text: string) => void;
  signal?: AbortSignal;
}

function ranges(state: UploadState, size: number): UploadLane[] {
  const lanes = state.lanes;
  const adaptive = state.protocol === ADAPTIVE_PROTOCOL;
  if ((!adaptive && state.protocol !== PARALLEL_PROTOCOL) || !(adaptive ? [2, 4, 8, 16] : [2, 4]).includes(state.parallelism ?? 0) || !lanes || lanes.length !== state.parallelism ||
      (adaptive && (!Number.isSafeInteger(state.concurrencyLimit) || state.concurrencyLimit! < 1 || state.concurrencyLimit! > lanes.length)) ||
      !Number.isSafeInteger(state.chunkSize) || state.chunkSize! < 1 || state.chunkSize! > MAX_CHUNK ||
      lanes.some(lane => !lane || ![lane.start, lane.end, lane.received].every(Number.isSafeInteger) ||
        lane.start < 0 || lane.end <= lane.start || lane.received < 0 || lane.received > lane.end - lane.start) ||
      lanes[0].start !== 0 || lanes[lanes.length - 1].end !== size ||
      lanes.some((lane, index) => index > 0 && lanes[index - 1].end !== lane.start) ||
      state.received !== lanes.reduce((sum, lane) => sum + lane.received, 0)) {
    throw new ApiError('Сервер не подтвердил диапазоны параллельной загрузки.', 502);
  }
  return lanes;
}

/** Durable ranges stay fixed on disk; active request count can adapt at any time. */
export async function uploadParallelRanges(options: Options): Promise<UploadState> {
  const { file, initial, read, send, onProgress, onStatus, signal } = options;
  const connectionLimit = () => Math.max(1, Math.min(16, options.connectionLimit?.() ?? 4));
  pageSlots.setLimit(connectionLimit());
  const initialLanes = ranges(initial, file.size).map(lane => ({ ...lane }));
  if (initial.received === file.size) return initial;
  if (!globalThis.crypto?.subtle) throw new ApiError('Для продолжения этой загрузки обновите браузер.', 400);
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.throwIfAborted();
  signal?.addEventListener('abort', abort, { once: true });
  let best = initial, failure: unknown;
  const adaptive = initial.protocol === ADAPTIVE_PROTOCOL
    ? new AdaptiveUploadConcurrency(initialLanes.length, Math.min(initial.concurrencyLimit!, connectionLimit())) : undefined;
  const localSlots = new UploadSlots(adaptive?.limit ?? initialLanes.length);
  const status = () => adaptive ? `Передача автоматически · потоков: ${adaptive.limit}`
    : initialLanes.length === 2 ? 'Передача в два потока' : 'Передача в четыре потока';
  onStatus?.(status());
  const worker = async (laneIndex: number) => {
    const lane = initialLanes[laneIndex];
    let offset = lane.start + lane.received;
    try {
      while (offset < lane.end) {
        controller.signal.throwIfAborted();
        pageSlots.setLimit(connectionLimit());
        const releaseLocal = await localSlots.acquire(controller.signal);
        let release: (() => void) | undefined;
        try {
          release = await pageSlots.acquire(controller.signal, adaptive ? 16 : 4);
          controller.signal.throwIfAborted();
          const end = Math.min(offset + initial.chunkSize!, lane.end);
          const blob = await read(offset, end, controller.signal);
          if (blob.size !== end - offset) throw new ApiError('Размер фрагмента файла изменился.', 409);
          const bytes = await blob.arrayBuffer();
          const checksum = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
          controller.signal.throwIfAborted();
          const chunk = new File([bytes], file.name, { type: 'application/octet-stream' });
          const state = await send(laneIndex, offset, chunk, checksum, controller.signal);
          controller.signal.throwIfAborted();
          const confirmed = ranges(state, file.size);
          if (confirmed.some((value, index) => value.start !== initialLanes[index].start || value.end !== initialLanes[index].end) ||
              confirmed[laneIndex].received !== end - lane.start || state.status !== 'uploading') {
            throw new ApiError('Сервер не подтвердил переданную часть файла.', 502);
          }
          offset = end;
          if (adaptive) localSlots.setLimit(adaptive.observe(chunk.size, Math.min(state.concurrencyLimit!, connectionLimit())));
          if (state.received > best.received) {
            best = state;
            onProgress?.(best.received);
          }
          onStatus?.(status());
        } finally { release?.(); releaseLocal(); }
      }
    } catch (error) {
      failure ??= error;
      controller.abort();
      throw error;
    }
  };
  try {
    await Promise.allSettled(initialLanes.map((_, index) => worker(index)));
    signal?.throwIfAborted();
    if (failure) throw failure instanceof Error ? failure : new ApiError('Не удалось передать часть файла.', 0);
    if (best.received !== file.size) throw new ApiError('Сервер не подтвердил полный размер файла.', 502);
    return best;
  } finally { signal?.removeEventListener('abort', abort); }
}
